<template>
    <div class="tn-page">

        <!-- Masthead -->
        <header class="tn-masthead">
            <div class="tn-masthead-left">
                <span class="tn-eyebrow">{{ dateRangeLabel }}</span>
                <h1 class="tn-title">Global News</h1>
            </div>
            <div class="tn-masthead-actions">
                <input
                    v-if="dateRangeMode === 'today'"
                    type="date"
                    class="tn-date-picker"
                    v-model="selectedDate"
                    :max="todayDate"
                    @change="onDateChange"
                />
                <button v-if="isLoggedIn" class="tn-settings-btn" @click="showSettings = true" title="LLM Settings">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                    </svg>
                </button>
                <button class="tn-refresh" :class="{ spinning: loading }" @click="fetchNews" :disabled="loading">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <polyline points="23 4 23 10 17 10"></polyline>
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                    </svg>
                    Refresh
                </button>
            </div>
        </header>

        <!-- Content: Summary sidebar + Main -->
        <div class="tn-content">
            <!-- Summary Sidebar -->
            <aside v-if="isLoggedIn" class="tn-sidebar">
                <summary-card
                    v-if="newsSummary"
                    :summary="newsSummary"
                    :collapsed="summaryCollapsed"
                    @select-topic="onTopicSelect"
                    @toggle="summaryCollapsed = !summaryCollapsed"
                />
                <button
                    class="tn-gen-btn"
                    :class="{ 'tn-gen-btn--loading': generatingSummary }"
                    :disabled="generatingSummary"
                    @click="generateSummary"
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
                    </svg>
                    <span v-if="generatingSummary">Generating...</span>
                    <span v-else-if="newsSummary">Regenerate</span>
                    <span v-else>Generate Summary</span>
                </button>
            </aside>

            <!-- Main: Toolbar + List + Footer -->
            <div class="tn-main">
                <!-- Toolbar -->
                <div class="tn-toolbar">
                    <div class="tn-search">
                        <svg class="tn-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <input
                            type="text"
                            class="tn-search-input"
                            v-model="searchQuery"
                            placeholder="Search by title..."
                            @input="onSearchInput"
                        />
                        <button v-if="searchQuery" class="tn-search-clear" @click="clearSearch">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                    <div class="tn-filter-group">
                        <select
                            class="tn-source-select"
                            v-model="selectedSource"
                            @change="onSourceChange"
                        >
                            <option value="">All Sources</option>
                            <option v-for="s in availableSources" :key="s" :value="s">
                                {{ s }}
                            </option>
                        </select>
                        <select
                            class="tn-country-select"
                            v-model="selectedCountry"
                            @change="onCountryChange"
                        >
                            <option value="">All Countries</option>
                            <option v-for="c in availableCountries" :key="c.code" :value="c.code">
                                {{ c.name }}
                            </option>
                        </select>
                        <div class="tn-date-tabs">
                            <button
                                v-for="tab in dateTabs"
                                :key="tab.value"
                                class="tn-date-tab"
                                :class="{ active: dateRangeMode === tab.value }"
                                @click="setDateRangeMode(tab.value)"
                            >{{ tab.label }}</button>
                        </div>
                    </div>
                </div>

                <!-- Loading -->
                <div v-if="loading" class="tn-list">
                    <div v-for="i in pageSize" :key="i" class="tn-skeleton" :style="{ animationDelay: (i * 0.03) + 's' }">
                        <div class="tn-skeleton-rank"></div>
                        <div class="tn-skeleton-body">
                            <div class="tn-skeleton-line" :style="{ width: (50 + (i * 13) % 40) + '%' }"></div>
                            <div class="tn-skeleton-sub"></div>
                        </div>
                    </div>
                </div>

                <!-- Error -->
                <div v-else-if="error" class="tn-error">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {{ error }}
                </div>

                <!-- Empty State -->
                <div v-else-if="filteredNews.length === 0" class="tn-empty">
                    <div class="tn-empty-content">
                        <div class="tn-empty-icon">
                            <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="1.5">
                                <rect x="8" y="12" width="48" height="44" rx="2" class="tn-empty-paper"/>
                                <line x1="16" y1="24" x2="48" y2="24" class="tn-empty-line tn-empty-line--1"/>
                                <line x1="16" y1="32" x2="40" y2="32" class="tn-empty-line tn-empty-line--2"/>
                                <line x1="16" y1="40" x2="44" y2="40" class="tn-empty-line tn-empty-line--3"/>
                                <line x1="16" y1="48" x2="32" y2="48" class="tn-empty-line tn-empty-line--4"/>
                                <circle cx="52" cy="8" r="6" class="tn-empty-dot"/>
                            </svg>
                        </div>
                        <h2 class="tn-empty-title">Nothing to Report</h2>
                        <p class="tn-empty-subtitle">The newsroom is quiet for {{ displayDate }}.</p>
                        <p class="tn-empty-hint">Try selecting a different date, or check back later.</p>
                        <button class="tn-empty-action" @click="goToToday">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="12 6 12 12 16 14"/>
                            </svg>
                            View Today
                        </button>
                    </div>
                </div>

                <!-- News list -->
                <ol v-else class="tn-list">
                    <li
                        v-for="(item, index) in pagedNews"
                        :key="globalIndex(index)"
                        class="tn-item"
                        :class="{ 'tn-item--hot': globalIndex(index) < 3 }"
                        :style="{ animationDelay: (index * 0.04) + 's' }"
                    >
                        <span class="tn-rank" :class="'tn-rank--' + Math.min(globalIndex(index) + 1, 4)">
                            {{ String(globalIndex(index) + 1).padStart(2, '0') }}
                        </span>
                        <div class="tn-body">
                            <div class="tn-title-row">
                                <a :href="item.url" target="_blank" rel="noopener noreferrer" class="tn-link">
                                    {{ item.title }}
                                </a>
                                <span v-if="item.source" class="tn-source">{{ item.source }}</span>
                                <span class="tn-domain">{{ extractDomain(item.url) }}</span>
                            </div>
                            <p class="tn-summary" :class="{ 'tn-summary--empty': !item.summary }">{{ item.summary || '\u00A0' }}</p>
                        </div>
                        <svg class="tn-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/>
                        </svg>
                    </li>
                </ol>

                <!-- Footer -->
                <footer v-if="filteredNews.length > 0" class="tn-footer">
                    <span class="tn-count">{{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, filteredNews.length) }} of {{ filteredNews.length }}</span>

                    <div class="tn-pager">
                        <button class="tn-pg-btn" :disabled="currentPage === 1" @click="currentPage--">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                        </button>

                        <span
                            class="tn-pg-num" :class="{ active: 1 === currentPage }"
                            @click="currentPage = 1"
                        >1</span>

                        <span v-if="visiblePages[0] > 2" class="tn-ellipsis">···</span>

                        <span
                            v-for="p in visiblePages" :key="p"
                            class="tn-pg-num" :class="{ active: p === currentPage }"
                            @click="currentPage = p"
                        >{{ p }}</span>

                        <span v-if="visiblePages[visiblePages.length - 1] < totalPages - 1" class="tn-ellipsis">···</span>

                        <span
                            v-if="totalPages > 1"
                            class="tn-pg-num" :class="{ active: totalPages === currentPage }"
                            @click="currentPage = totalPages"
                        >{{ totalPages }}</span>

                        <button class="tn-pg-btn" :disabled="currentPage === totalPages" @click="currentPage++">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                    </div>

                    <div class="tn-sizes">
                        <span
                            v-for="s in [10, 20, 50]" :key="s"
                            class="tn-size" :class="{ active: pageSize === s }"
                            @click="setPageSize(s)"
                        >{{ s }}</span>
                        <span class="tn-size-unit">/ page</span>
                    </div>
                </footer>
            </div>
        </div>
        </div>

        <settings-modal
            :visible="showSettings"
            @close="showSettings = false"
            @saved="onSettingsSaved"
        />
</template>

<script>
import SummaryCard from './SummaryCard.vue';
import SettingsModal from './SettingsModal.vue';
import { isAuthenticated, getToken, clearAuth } from '../../utils/auth';

export default {
    name: 'TopNews',
    components: { SummaryCard, SettingsModal },
    data() {
        return {
            news: [],
            activeTopic: null,
            loading: false,
            error: '',
            selectedDate: new Date().toISOString().slice(0, 10),
            todayDate: new Date().toISOString().slice(0, 10),
            currentPage: 1,
            pageSize: 10,
            // Search & Filter
            searchQuery: '',
            searchDebounceTimer: null,
            selectedCountry: '',
            selectedSource: '',
            dateRangeMode: 'today', // 'today' | '3days' | '7days' | '30days'
            availableCountries: [],
            availableSources: [],
            newsSummary: null,
            summaryCollapsed: false,
            showSettings: false,
            generatingSummary: false,
            isLoggedIn: isAuthenticated(),
        };
    },
    computed: {
        dateTabs() {
            return [
                { label: 'Today', value: 'today' },
                { label: '3 Days', value: '3days' },
                { label: '7 Days', value: '7days' },
                { label: '30 Days', value: '30days' },
            ];
        },
        dateRangeDays() {
            const map = { today: 1, '3days': 3, '7days': 7, '30days': 30 };
            return map[this.dateRangeMode] || 1;
        },
        dateRangeLabel() {
            if (this.dateRangeMode === 'today') {
                return `TODAY · ${this.displayDate}`;
            }
            return `LAST ${this.dateRangeDays} DAYS`;
        },
        filteredNews() {
            let result = this.news;
            if (this.selectedSource) {
                result = result.filter(item => item.source === this.selectedSource);
            }
            if (this.activeTopic) {
                const highlights = (this.activeTopic.highlights || []).map(h => {
                    let clean = h.replace(/^\[.*?\]\s*/, '').replace(/&#\w+;/g, "'").toLowerCase();
                    return clean;
                });
                if (highlights.length) {
                    result = result.filter(item => {
                        const title = (item.title || '').toLowerCase();
                        return highlights.some(h => title.includes(h) || h.includes(title));
                    });
                }
            }
            return result;
        },
        pagedNews() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.filteredNews.slice(start, start + this.pageSize);
        },
        visiblePages() {
            const total = this.totalPages;
            const current = this.currentPage;
            const delta = 1; // pages around current
            const pages = [];

            // Calculate range: current - delta to current + delta
            // But exclude 1 and total (they're shown separately)
            const start = Math.max(2, current - delta);
            const end = Math.min(total - 1, current + delta);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        },
        totalPages() {
            return Math.ceil(this.filteredNews.length / this.pageSize);
        },
        displayDate() {
            const d = new Date(this.selectedDate + 'T00:00:00');
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        },
    },
    mounted() {
        this._onResize = () => {
            if (window.innerWidth <= 860) {
                this.summaryCollapsed = true;
            }
        };
        this._onResize();
        window.addEventListener('resize', this._onResize);
        this.checkAuth();
        this.fetchNews();
        this.fetchSummary();
        this._onKeydown = (e) => {
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
            if (e.key === 'ArrowLeft' && this.currentPage > 1) {
                this.currentPage--;
                e.preventDefault();
            } else if (e.key === 'ArrowRight' && this.currentPage < this.totalPages) {
                this.currentPage++;
                e.preventDefault();
            }
        };
        window.addEventListener('keydown', this._onKeydown);
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this._onKeydown);
        window.removeEventListener('resize', this._onResize);
    },
    methods: {
        async checkAuth() {
            if (!isAuthenticated()) {
                this.isLoggedIn = false;
                return;
            }
            try {
                const token = getToken();
                const res = await fetch('https://xtool-auth.gson.workers.dev/auth/verify', {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                const data = await res.json();
                if (!data.valid) {
                    clearAuth();
                    this.isLoggedIn = false;
                    window.dispatchEvent(new CustomEvent('auth-change'));
                } else {
                    this.isLoggedIn = true;
                }
            } catch {
                this.isLoggedIn = false;
            }
        },
        async fetchNews() {
            this.loading = true;
            this.error = '';
            try {
                // Build query params
                const params = new URLSearchParams();
                if (this.searchQuery) params.set('q', this.searchQuery);
                if (this.selectedCountry) params.set('country', this.selectedCountry);
                if (this.dateRangeMode !== 'today') params.set('days', this.dateRangeDays);

                // Always use query params format to get meta.countries
                params.set('date', this.selectedDate);
                const url = `https://economic-news-crawler.gson.workers.dev/news?${params.toString()}`;

                const res = await fetch(url);
                if (res.status === 404) {
                    this.news = [];
                } else if (!res.ok) {
                    throw new Error(`HTTP ${res.status}`);
                } else {
                    const data = await res.json();
                    // Handle both old format (array) and new format ({ data, meta })
                    let newsData = Array.isArray(data) ? data : (data.data || []);
                    // Extract countries from meta if available
                    if (data.meta && data.meta.countries) {
                        this.availableCountries = data.meta.countries.map(code => ({
                            code,
                            name: this.getCountryName(code)
                        }));
                    }
                    // Transform data format: link -> url
                    this.news = newsData.map(item => ({
                        ...item,
                        url: item.link || item.url
                    }));
                    this.availableSources = [...new Set(this.news.map(item => item.source).filter(Boolean))].sort();
                }
            } catch (e) {
                this.error = 'Failed to fetch news: ' + e.message;
            } finally {
                this.loading = false;
                this.fetchSummary();
            }
        },
        onSearchInput() {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(() => {
                this.currentPage = 1;
                this.activeTopic = null;
                this.fetchNews();
            }, 300);
        },
        clearSearch() {
            this.searchQuery = '';
            this.currentPage = 1;
            this.activeTopic = null;
            this.fetchNews();
        },
        onCountryChange() {
            this.currentPage = 1;
            this.activeTopic = null;
            this.fetchNews();
        },
        onSourceChange() {
            this.currentPage = 1;
            this.activeTopic = null;
        },
        setDateRangeMode(mode) {
            this.dateRangeMode = mode;
            this.currentPage = 1;
            this.activeTopic = null;
            this.fetchNews();
        },
        getCountryName(code) {
            const names = {
                CN: 'China',
                US: 'United States',
                JP: 'Japan',
                KR: 'South Korea',
                UK: 'United Kingdom',
                DE: 'Germany',
                FR: 'France',
                AU: 'Australia',
                CA: 'Canada',
                IN: 'India',
                SG: 'Singapore',
                HK: 'Hong Kong',
                TW: 'Taiwan',
            };
            return names[code] || code;
        },
        onDateChange() {
            this.currentPage = 1;
            this.fetchNews();
        },
        globalIndex(i) {
            return (this.currentPage - 1) * this.pageSize + i;
        },
        setPageSize(s) {
            this.pageSize = s;
            this.currentPage = 1;
        },
        goToToday() {
            this.selectedDate = this.todayDate;
            this.currentPage = 1;
            this.fetchNews();
        },
        extractDomain(url) {
            try { return new URL(url).hostname.replace('www.', ''); } catch { return ''; }
        },
        async fetchSummary() {
            try {
                const params = new URLSearchParams();
                if (this.dateRangeMode === 'today') {
                    params.set('date', this.selectedDate);
                } else {
                    params.set('days', this.dateRangeDays);
                }
                const url = `https://economic-news-crawler.gson.workers.dev/news/summary?${params.toString()}`;
                const res = await fetch(url);
                if (res.ok) {
                    const data = await res.json();
                    this.newsSummary = data.data ? data.data[0] || null : data;
                } else {
                    this.newsSummary = null;
                }
            } catch {
                this.newsSummary = null;
            }
        },
        onTopicSelect(topicName) {
            if (this.activeTopic?.name === topicName) {
                this.activeTopic = null;
                this.currentPage = 1;
                return;
            }
            const topic = this.newsSummary?.topics?.find(t => t.name === topicName);
            this.activeTopic = topic || null;
            this.searchQuery = '';
            this.currentPage = 1;
        },
        onSettingsSaved() {
            this.showSettings = false;
        },
        async generateSummary() {
            if (this.generatingSummary) return;
            this.generatingSummary = true;

            try {
                const token = getToken();
                if (!token) {
                    this.$router.push('/userLogin');
                    return;
                }

                const date = this.dateRangeMode === 'today' ? this.selectedDate : new Date().toISOString().slice(0, 10);
                const res = await fetch(`https://economic-news-crawler.gson.workers.dev/run/summary?date=${date}`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                });

                if (res.status === 401) {
                    clearAuth();
                    this.isLoggedIn = false;
                    this.$router.push('/userLogin');
                    return;
                }

                const data = await res.json();
                if (res.ok && data.summary) {
                    this.newsSummary = data.summary;
                } else {
                    this.error = data.error || 'Failed to generate summary';
                }
            } catch (e) {
                this.error = 'Generation failed: ' + e.message;
            } finally {
                this.generatingSummary = false;
            }
        },
    },
};
</script>

<style scoped>
/* ── Page (fixed below navbar) ── */
.tn-page {
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    bottom: 0;
    max-width: 1200px;
    margin: 0 auto;
    padding: 10px 24px 6px;
    font-family: var(--font-body);
    color: var(--text-primary);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.tn-content {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    flex: 1;
    min-height: 0;
}

.tn-sidebar {
    width: 300px;
    flex-shrink: 0;
    position: sticky;
    top: 0;
    max-height: 100%;
    overflow-y: auto;
    scrollbar-width: thin;
}

.tn-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

@media (max-width: 860px) {
    .tn-content {
        flex-direction: column;
    }
    .tn-sidebar {
        width: 100%;
        position: static;
        max-height: none;
        order: 2;
    }
    .tn-main {
        order: 1;
    }
}

.tn-gen-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: var(--font-body);
    font-size: 11px;
    font-weight: 500;
    padding: 5px 12px;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    background: var(--bg-card);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
    margin-top: 8px;
    width: 100%;
    justify-content: center;
}
.tn-gen-btn:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
}
.tn-gen-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.tn-gen-btn--loading svg {
    animation: spin 0.8s linear infinite;
}

/* ── Masthead ── */
.tn-masthead {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 10px;
    flex-shrink: 0;
}

.tn-masthead-left {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.tn-masthead-actions {
    display: flex;
    align-items: center;
    gap: 6px;
}

.tn-date-picker {
    font-family: var(--font-mono);
    font-size: 11px;
    padding: 4px 8px;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    background: var(--bg-card);
    color: var(--text-primary);
    cursor: pointer;
    transition: all var(--transition-fast);
}

.tn-date-picker:hover {
    border-color: var(--color-primary-light);
}

.tn-date-picker:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.tn-eyebrow {
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.12em;
    color: var(--text-tertiary);
    text-transform: uppercase;
    line-height: 1.3;
}

.tn-title {
    font-family: var(--font-heading);
    font-size: 22px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0;
    letter-spacing: -0.3px;
    line-height: 1.1;
}

.tn-refresh {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
    font-family: var(--font-body);
    font-size: 12px;
    font-weight: 500;
    padding: 4px 10px;
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all var(--transition-fast);
}

.tn-refresh:hover:not(:disabled) {
  border-color: var(--color-primary);
  color: var(--color-primary);
  box-shadow: var(--shadow-md);
}

.tn-refresh:disabled { opacity: 0.5; cursor: not-allowed; }

@keyframes spin { to { transform: rotate(360deg); } }
.tn-refresh.spinning svg { animation: spin 0.8s linear infinite; }

.tn-settings-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
    padding: 4px;
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all var(--transition-fast);
}

.tn-settings-btn:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
}

/* ── Toolbar ── */
.tn-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
    padding: 8px 12px;
    background: var(--bg-card);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm);
    border: 1px solid var(--border-light);
    flex-shrink: 0;
}

.tn-search {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
}

.tn-search-icon {
    position: absolute;
    left: 10px;
    color: var(--text-disabled);
    pointer-events: none;
}

.tn-search-input {
    width: 100%;
    font-family: var(--font-body);
    font-size: 12px;
    padding: 5px 28px 5px 28px;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    background: var(--bg-secondary);
    color: var(--text-primary);
    transition: all var(--transition-fast);
}

.tn-search-input::placeholder {
    color: var(--text-disabled);
}

.tn-search-input:focus {
    outline: none;
    border-color: var(--color-primary);
    background: var(--bg-card);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.tn-search-clear {
    position: absolute;
    right: 8px;
    background: transparent;
    border: none;
    color: var(--text-disabled);
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color var(--transition-fast);
}

.tn-search-clear:hover {
    color: var(--text-secondary);
}

.tn-filter-group {
    display: flex;
    align-items: center;
    gap: 6px;
}

.tn-source-select {
    font-family: var(--font-body);
    font-size: 11px;
    padding: 5px 24px 5px 8px;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    background: var(--bg-secondary);
    color: var(--text-primary);
    cursor: pointer;
    transition: all var(--transition-fast);
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 6px center;
    max-width: 140px;
}

.tn-source-select:hover {
    border-color: var(--color-primary-light);
}

.tn-source-select:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.tn-country-select {
    font-family: var(--font-body);
    font-size: 11px;
    padding: 5px 24px 5px 8px;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    background: var(--bg-secondary);
    color: var(--text-primary);
    cursor: pointer;
    transition: all var(--transition-fast);
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 6px center;
}

.tn-country-select:hover {
    border-color: var(--color-primary-light);
}

.tn-country-select:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.tn-date-tabs {
    display: flex;
    gap: 2px;
    background: var(--bg-secondary);
    padding: 2px;
    border-radius: var(--radius-md);
}

.tn-date-tab {
    font-family: var(--font-body);
    font-size: 10px;
    font-weight: 500;
    padding: 3px 8px;
    border: none;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
}

.tn-date-tab:hover {
    color: var(--text-primary);
}

.tn-date-tab.active {
    background: var(--color-primary);
    color: var(--text-inverse);
}

/* ── Divider line ── */
.tn-page::before {
    content: none;
}

/* ── List container (internal scroll) ── */
.tn-list {
    background: var(--bg-card);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-md);
    overflow-y: auto;
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid var(--border-light);
    flex: 1;
}
.tn-list::-webkit-scrollbar {
    width: 5px;
}
.tn-list::-webkit-scrollbar-track {
    background: transparent;
}
.tn-list::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 3px;
}

/* ── Skeleton ── */
@keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.8; }
}

.tn-skeleton {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    border-bottom: 1px solid var(--border-light);
    animation: pulse 1.6s ease-in-out infinite;
}

.tn-skeleton-rank {
    width: 20px;
    height: 20px;
    border-radius: var(--radius-sm);
    background: var(--bg-secondary);
    flex-shrink: 0;
}

.tn-skeleton-body { flex: 1; display: flex; flex-direction: column; gap: 8px; }

.tn-skeleton-line {
    height: 13px;
    background: var(--bg-secondary);
    border-radius: 2px;
}

.tn-skeleton-sub {
    width: 120px;
    height: 10px;
    background: var(--bg-secondary);
    border-radius: 2px;
    opacity: 0.6;
}

/* ── Error ── */
.tn-error {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: var(--color-error);
    font-size: 12px;
    flex: 1;
    margin: 0;
}

/* ── Empty State ── */
.tn-empty {
    background: var(--bg-card);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-md);
    padding: 32px 24px;
    text-align: center;
    position: relative;
    overflow: hidden;
    border: 1px solid var(--border-light);
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
}

.tn-empty::before {
  content: '';
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(ellipse at 50% 0%, rgba(59, 130, 246, 0.04) 0%, transparent 60%),
    linear-gradient(180deg, transparent 0%, var(--bg-secondary) 100%);
  opacity: 0.5;
  pointer-events: none;
}

.tn-empty::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--border-color), transparent);
}

.tn-empty-content {
    position: relative;
    z-index: 1;
    max-width: 320px;
    margin: 0 auto;
}

.tn-empty-icon {
    margin-bottom: 16px;
    color: var(--border-color);
    transition: color var(--transition-normal);
}

.tn-empty:hover .tn-empty-icon {
    color: var(--text-disabled);
}

.tn-empty-icon svg {
    width: 56px;
    height: 56px;
    margin: 0 auto;
    display: block;
}

.tn-empty-line {
    stroke: var(--text-disabled);
    opacity: 0.3;
    stroke-linecap: round;
    transform-origin: left center;
}

.tn-empty-line--1 { animation: emptyPulse 3s ease-in-out infinite; animation-delay: 0s; }
.tn-empty-line--2 { animation: emptyPulse 3s ease-in-out infinite; animation-delay: 0.4s; }
.tn-empty-line--3 { animation: emptyPulse 3s ease-in-out infinite; animation-delay: 0.8s; }
.tn-empty-line--4 { animation: emptyPulse 3s ease-in-out infinite; animation-delay: 1.2s; }

@keyframes emptyPulse {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 0.5; }
}

.tn-empty-paper {
    stroke: var(--border-color);
    fill: var(--bg-card);
    animation: paperFloat 6s ease-in-out infinite;
    transform-origin: center center;
}

@keyframes paperFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
}

.tn-empty-dot {
    fill: none;
    stroke: var(--color-primary);
    opacity: 0.5;
    animation: dotBlink 2s ease-in-out infinite;
}

@keyframes dotBlink {
    0%, 100% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(1.1); }
}

.tn-empty-title {
    font-family: var(--font-heading);
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 6px;
    letter-spacing: -0.3px;
    animation: fadeUp 0.5s ease both;
    animation-delay: 0.1s;
}

.tn-empty-subtitle {
    font-family: var(--font-body);
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0 0 6px;
    animation: fadeUp 0.5s ease both;
    animation-delay: 0.2s;
}

.tn-empty-hint {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-tertiary);
    margin: 0 0 28px;
    letter-spacing: 0.02em;
    animation: fadeUp 0.5s ease both;
    animation-delay: 0.3s;
}

.tn-empty-action {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
    font-family: var(--font-body);
    font-size: 13px;
    font-weight: 500;
    padding: 10px 20px;
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all var(--transition-fast);
    animation: fadeUp 0.5s ease both;
    animation-delay: 0.4s;
}

.tn-empty-action:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.tn-empty-action:active {
    transform: translateY(0);
}

/* ── News item ── */
@keyframes fadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
}

.tn-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 14px;
    border-bottom: 1px solid var(--border-light);
    cursor: default;
    animation: fadeUp 0.35s ease both;
    transition: background var(--transition-fast);
    position: relative;
}

.tn-item:last-child { border-bottom: none; }

.tn-item:hover { background: var(--bg-secondary); }

.tn-item--hot .tn-link {
    font-weight: 500;
    color: var(--text-primary);
}

/* ── Rank badge ── */
.tn-rank {
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 600;
    min-width: 20px;
    height: 20px;
    margin-top: 1px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-sm);
    flex-shrink: 0;
    letter-spacing: 0;
}

.tn-rank--1 { background: rgba(59, 130, 246, 0.1); color: var(--color-primary); }
.tn-rank--2 { background: rgba(59, 130, 246, 0.07); color: var(--color-primary-light); }
.tn-rank--3 { background: rgba(59, 130, 246, 0.04); color: var(--text-tertiary); }
.tn-rank--4 { background: var(--bg-secondary); color: var(--text-disabled); }

/* ── Body ── */
.tn-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.tn-title-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.tn-link {
    font-size: 13px;
    font-weight: 400;
    color: var(--text-primary);
    text-decoration: none;
    line-height: 1.45;
    transition: color var(--transition-fast);
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

.tn-item:hover .tn-link {
  color: var(--color-primary);
}

.tn-source {
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 500;
  color: var(--color-primary);
  background: rgba(59, 130, 246, 0.08);
  padding: 1px 6px;
  border-radius: 3px;
  flex-shrink: 0;
  white-space: nowrap;
}

.tn-domain {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--text-tertiary);
    letter-spacing: 0.02em;
    flex-shrink: 0;
    white-space: nowrap;
}

.tn-summary {
    font-size: 11px;
    color: var(--text-tertiary);
    line-height: 1.45;
    margin: 1px 0 0 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

/* ── Arrow ── */
.tn-arrow {
    color: var(--border-color);
    flex-shrink: 0;
    opacity: 0;
    transform: translate(-3px, 3px);
    transition: all var(--transition-fast);
    margin-top: 4px;
}

.tn-item:hover .tn-arrow {
  opacity: 1;
  color: var(--color-primary);
  transform: translate(0, 0);
}

/* ── Footer ── */
.tn-footer {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 6px;
    padding: 6px 0 8px;
    flex-shrink: 0;
}

.tn-count {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--text-tertiary);
    flex: 1;
    letter-spacing: 0.04em;
}

.tn-pager {
    display: flex;
    align-items: center;
    gap: 2px;
}

.tn-pg-btn {
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
    font-size: 13px;
    line-height: 1;
    width: 24px;
    height: 24px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all var(--transition-fast);
}

.tn-pg-btn:hover:not(:disabled) {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.tn-pg-btn:disabled { opacity: 0.3; cursor: not-allowed; }

.tn-pg-num {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-secondary);
    width: 24px;
    height: 24px;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all var(--transition-fast);
}

.tn-pg-num:hover { color: var(--color-primary); background: var(--bg-hover); }

.tn-pg-num.active {
  background: var(--color-primary);
  color: var(--text-inverse);
  border-color: var(--color-primary);
}

.tn-ellipsis {
    font-family: var(--font-mono);
    font-size: 9px;
    color: var(--text-disabled);
    letter-spacing: 1px;
    width: 20px;
    text-align: center;
    user-select: none;
}

.tn-sizes {
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: var(--font-mono);
    font-size: 10px;
}

.tn-size {
    color: var(--text-disabled);
    cursor: pointer;
    padding: 1px 5px;
    border-radius: var(--radius-sm);
    transition: all var(--transition-fast);
    border: 1px solid transparent;
}

.tn-size:hover { color: var(--text-secondary); background: var(--bg-hover); }

.tn-size.active {
  color: var(--color-primary);
  border-color: var(--color-primary);
  background: rgba(59, 130, 246, 0.08);
}

.tn-size-unit { color: var(--text-disabled); }
</style>
